// THE LIVING COVER'S FLOATING MATERIAL, ONE ENGINE (owner 2026-10-17). The
// website draws it live in the browser and the server draws the downloads
// with exactly this file (services/quicktools/float-render.js loads it with
// @napi-rs/canvas), so the page shows what the file has.
//
// How each kind moves (owner, 2026-10-17): "all overlay contant should slowly
// float downwards with exception to the bullet holes, the light from above
// (which should only emit light downward), and smoke (which should move how
// smoke naturally does), and the embers (which should move as if emitted off
// of a fire below)", never fast ("material should never be ... moving fast").
//
//   fall   drifts slowly down (40-200 px a loop), swaying and turning a little,
//          fading in at the top of its path and out at the bottom;
//   rise   embers: born low, rising and swaying, flickering, shrinking and
//          reddening as they cool;
//   smoke  soft puffs that rise, spread, turn and fade, staggered, so no part
//          of the picture stays covered;
//   light  the beam from above, steady, with rays sliding down inside it;
//   still  bullet holes.
//
// Everything is a function of the time modulo the 7.5 s loop, so the last
// frame of a download meets the first. Each thing lives once (or twice) per
// loop at its own phase; what wraps does so while faded out.

export const LOOP_S = 7.5;
export const FRAME = { w: 1080, h: 1920 };
// The frame the scene being built or drawn is in (owner 2026-10-23: every shape is drawn in its own size, 4:5, square,
// 16:9...): buildScene's `frame`, kept on the scene; drawScene and positions set it again from the scene (all
// synchronous, so one scene at a time). Sizes are shares of the frame's SHORT side, so a bill is the same size in
// every shape, and counts follow the frame's area.
let F = FRAME;
const unit = () => Math.min(F.w, F.h);
const TAU = Math.PI * 2;

// size: [far, near] longest side as a share of the frame width (pieces), or a
// multiple of the piece's size in the owner's picture (ref: true).
// fall: [far, near] pixels a loop. sway: pixels side to side. spin: radians a
// loop. turn: the start angle range. tumble: paper and cards flip as they fall.
// light: drawn as light (adds), else laid on. solid: keeps the picture's glow
// off it (bills, cards, metal).
export const KINDS = {
  // More and larger than first built (owner 2026-10-20: "the dust overlay doesnt even appear at all": 70 specks of
  // 13-20 px, nearly see-through, vanished on a picture). maxScale: how far past its own pixels a speck may grow.
  dust: { src: 'dust', motion: 'fall', light: true, count: 130, ref: true, size: [0.8, 1.6], fall: [40, 90], sway: 8, spin: 0.4, maxScale: 2.6 },
  fine: { draw: 'dot', motion: 'fall', light: true, count: 220, size: [1.4, 3.4], fall: [30, 70], sway: 5 },
  // Dust in the light (owner 2026-10-20, his reference canvas: "you can see like actual dust particles in the light"):
  // soft specks drifting down slower than anything else, glinting as they turn, seen only where the picture is
  // lit (the scene's light map, lightMap()); in the dark parts of the picture they are not there.
  motes: { draw: 'mote', motion: 'fall', light: true, lit: true, count: 260, size: [0.9, 2.2], fall: [14, 36], sway: 9 },
  smoke: { src: 'smoke', motion: 'smoke', light: true, count: 9 },
  'smoke-fire': { src: 'smoke-fire', motion: 'smoke', light: true, count: 9 },
  'smoke-red': { src: 'smoke-red', motion: 'smoke', light: true, count: 9 },
  rain: { src: 'rain', motion: 'fall', light: true, count: 300, ref: true, size: [1.1, 1.5], fall: [90, 170], sway: 0, spin: 0, turn: 0 },
  light: { src: 'light', motion: 'light', light: true, count: 8 },
  // The owner's reference (2026-10-18, "i want the embers effect exactly like this"): hundreds of thin short
  // spark streaks shooting up off a fire below, slanted, orange, red and a few yellow-white, twinkling, thick low
  // and thinning out high, over a dim orange glow rising from the bottom.
  embers: { draw: 'ember', motion: 'rise', light: true, count: 170 },
  // through: the big things (bills, burning bills, cards) cross the whole frame once a loop, coming in above
  // the top and leaving below the bottom, so they never vanish in mid-air (owner 2026-10-18: "as the other
  // overlays are floating downward they just vanish in thin air and than reappear somewhere else that looks
  // stupid" ... "let the bigger stuff like the bills and the cards fall faster so it aligns with the 7.5
  // seconds... the littler stuff can stay the same because its not so obvious"; then "no bullets too they are too big
  // to disappear", "and glass").
  money: { src: 'money', motion: 'fall', through: true, solid: true, count: 14, size: [0.13, 0.3], fall: [70, 190], sway: 26, spin: 0.9, turn: 0.6, tumble: true },
  'money-red': { src: 'money-red', motion: 'fall', through: true, solid: true, count: 14, size: [0.13, 0.3], fall: [70, 190], sway: 26, spin: 0.9, turn: 0.6, tumble: true },
  burning: { src: 'burning', motion: 'fall', through: true, solid: true, count: 7, size: [0.2, 0.34], fall: [50, 120], sway: 18, spin: 0.4, turn: 0.25, burn: true },
  cards: { src: 'cards', motion: 'fall', through: true, solid: true, count: 12, size: [0.1, 0.2], fall: [70, 180], sway: 24, spin: 1.2, turn: 0.8, tumble: true },
  bullets: { src: 'bullets', motion: 'fall', through: true, solid: true, count: 12, size: [0.1, 0.2], fall: [60, 160], sway: 12, spin: 1.1, turn: 3.1 },
  casings: { src: 'casings', motion: 'fall', through: true, solid: true, count: 14, size: [0.07, 0.14], fall: [60, 160], sway: 12, spin: 1.4, turn: 3.1 },
  // Glass is a still pane, his picture of a shattered window over the whole frame (owner 2026-10-18: "replace the
  // falling glass with this still overlay"): the black is see-through, the cracked glass stays.
  glass: { src: 'glass', motion: 'pane', light: true, count: 1 },
  // The owner's two Pinterest pictures (2026-10-19: "add these two over lays exactly"), still panes like the glass.
  'glass-holes': { src: 'glass-holes', motion: 'pane', light: true, count: 1 },
  bulletholes: { src: 'bulletholes', motion: 'still', light: true, count: 3, size: [0.3, 0.42], turn: 3.1 },
  'bulletholes-red': { src: 'bulletholes-red', motion: 'pane', light: true, count: 1 },
  math: { src: 'math', motion: 'fall', light: true, count: 12, size: [0.18, 0.4], fall: [40, 110], sway: 10, spin: 0.15, turn: 0.12 },
  confetti: { draw: 'confetti', motion: 'fall', count: 70, size: [10, 22], fall: [60, 150], sway: 22, spin: 2.5, tumble: true },
  // The owner's clips and the library he picked (2026-10-18, 2026-10-20) are videos, not drawn here: they are listed
  // in overlay-library.json (particles.js VIDEO_KINDS on the server, LivingCover's <video> on the page).
};

export const AMOUNTS = { light: 0.55, normal: 1, heavy: 1.7 };
/**
 * How much floats (owner 2026-10-20: slide bars "that actually work and are actually accurate"): the slider's 0..100,
 * or the old words. 50 is the old Normal (x1), 0 almost nothing, 100 twice as much. The page and the downloads both
 * draw from this number, so the preview and the file match.
 */
export function amountMult(amount) {
  if (AMOUNTS[amount]) return AMOUNTS[amount];
  const v = Number(amount);
  if (!Number.isFinite(v)) return 1;
  return Math.max(0.05, Math.min(2, v / 50));
}
/**
 * WHERE AN OVERLAY SITS (owner 2026-10-24: "the overlay has a transparency bar, can be enlarged and moveable around the
 * screen just like the app inshot does their overlays"): { x, y, s }, the overlay's middle moved from the frame's middle
 * by x of the frame's width and y of its height, the whole overlay s times the frame's size (1 the frame). The page's
 * clips (CSS), the page's drawn things (drawScene), the file's clips (canvas.js floatPass) and the file's drawn things
 * (this file again) all place it from placeBox, so the preview and the file put it in the same spot.
 */
export const PLACE = { minS: 0.25, maxS: 4, maxXY: 1, feather: 0.06 };
/** A placement that changes nothing (left out of the form). */
export function placeIsNone(p) {
  return !p || (Math.abs(p.x) < 5e-4 && Math.abs(p.y) < 5e-4 && Math.abs(p.s - 1) < 5e-3 && !placeTurn(p));
}
/**
 * How far a place is turned (owner 2026-10-27: "the overlay ... isnt able to be rotated"): r, degrees clockwise about
 * the overlay's middle, in -180..180; 0 when not turned. The same numbers as particles.js placeTurn.
 */
export function placeTurn(p) {
  const r = Number(p && p.r);
  if (!Number.isFinite(r)) return 0;
  const d = ((((r + 180) % 360) + 360) % 360) - 180;
  return Math.abs(d) < 0.05 ? 0 : d;
}
/**
 * The overlay's box in the frame (frame px): left, top, w, h, and how soft each edge is (fl, fr, ft, fb, frame px): an
 * edge inside the frame fades over up to PLACE.feather of the frame's short side, so a moved or shrunk overlay never
 * shows a hard edge; an edge at or past the frame's border is not faded (the more of it inside, the softer, so moving
 * it in is smooth). The same numbers as particles.js placeBox (tests/quicktools.test.js keeps them in step).
 */
export function placeBox(p, frame = FRAME) {
  const W = frame.w;
  const H = frame.h;
  const s = p.s;
  const w = W * s;
  const h = H * s;
  const left = (W - w) / 2 + p.x * W;
  const top = (H - h) / 2 + p.y * H;
  const f = PLACE.feather * Math.min(W, H);
  const edge = (inside, size) => Math.max(0, Math.min(f, inside, size / 2));
  // Turned, every edge may be inside the frame: all four fade.
  if (placeTurn(p)) {
    const all = Math.max(0, Math.min(f, w / 2, h / 2));
    return { left, top, w, h, s, fl: all, fr: all, ft: all, fb: all };
  }
  return { left, top, w, h, s, fl: edge(left, w), fr: edge(W - left - w, w), ft: edge(top, h), fb: edge(H - top - h, h) };
}

/** The smoke's thickness for that amount (the old words: 0.6 / 1 / 1.25). */
export function amountAlpha(amount) {
  const m = amountMult(amount);
  return Math.max(0.35, Math.min(1.35, 0.55 + 0.42 * m));
}

/**
 * THE PICTURE'S LIGHT, for the dust seen only in it (motes): the picture cover-fit to 9:16 at LIGHT_MAP's size, as
 * RGBA bytes (the server: sharp; the page: a small canvas), turned into how lit each spot is, 0..1. Softened by
 * two box blurs, so a speck fades in and out at the edge of the light instead of popping.
 */
export const LIGHT_MAP = { w: 54, h: 96 };
export function lightMap(rgba) {
  const { w, h } = LIGHT_MAP;
  let v = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) v[i] = (0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2]) / 255;
  for (let pass = 0; pass < 2; pass++) {
    const o = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sum = 0;
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx;
            const yy = y + dy;
            if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
            sum += v[yy * w + xx];
            n++;
          }
        }
        o[y * w + x] = sum / n;
      }
    }
    v = o;
  }
  return { w, h, v };
}
/**
 * How lit the frame is at (x, y): 0 in the black, 1 wherever the picture is lit at all (from ~0.1, full by ~0.35), as
 * in the owner's reference, where the specks drift over his hair, face and blanket and never over the black behind.
 */
function litAt(map, x, y) {
  const fx = Math.max(0, Math.min(map.w - 1.001, (x / F.w) * map.w - 0.5));
  const fy = Math.max(0, Math.min(map.h - 1.001, (y / F.h) * map.h - 0.5));
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const ax = fx - x0;
  const ay = fy - y0;
  const at = (xx, yy) => map.v[yy * map.w + xx];
  const v = (at(x0, y0) * (1 - ax) + at(x0 + 1, y0) * ax) * (1 - ay) + (at(x0, y0 + 1) * (1 - ax) + at(x0 + 1, y0 + 1) * ax) * ay;
  return smooth((v - 0.1) / 0.25);
}

function rng(seed) {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5;
    s >>>= 0;
    return s / 4294967296;
  };
}

const lerp = (a, b, k) => a + (b - a) * k;
const smooth = (x) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));
/** In over the first `a` of a life, out over the last `b`. */
const envelope = (u, a = 0.18, b = 0.18) => Math.min(smooth(u / a), smooth((1 - u) / b));

/** Pieces rarely land over the middle, where the subject's face usually is. */
function spot(r) {
  let x = r();
  let y = r();
  for (let i = 0; i < 8 && Math.hypot((x - 0.5) / 0.3, (y - 0.4) / 0.24) < 1 && r() < 0.9; i++) {
    x = r();
    y = r();
  }
  return [x, y];
}

/**
 * The things of the kinds picked, placed once from a seed (the same seed draws
 * the same field on the page and in the download).
 * `assets[name]` = { image, pieces: [[x,y,w,h],...], refWidth } for pieces,
 * { image } for a whole picture (light, smoke). makeCanvas(w,h) builds the
 * smoke puffs' soft-edged copies. opacity: each kind's own see-through slider ({ kind: 0..1 }, 1 or absent solid;
 * owner 2026-10-21: "we need a sliding transparency"), read again at every draw so the page can change it live.
 * place: where each kind sits ({ kind: { x, y, s } }, placeBox; absent: the whole frame), read at every draw too.
 */
export function buildScene({ kinds, amount = 'normal', seed = 7, assets, makeCanvas, preview = false, light = null, opacity = null, place = null, frame = FRAME }) {
  F = frame || FRAME;
  const r = rng(seed);
  const mult = amountMult(amount) * (preview ? 0.5 : 1) * ((F.w * F.h) / (FRAME.w * FRAME.h));
  // The kinds that are one picture (the beam, the panes, the fire's glow) can't be more or fewer pieces: the slider
  // makes them fainter or stronger instead (owner 2026-10-20: "the slide bars for amounts of overlay doesnt work for
  // all of them"). 0..2, 1 at the middle; above 1 a light layer is laid on twice (drawScene).
  const gain = amountMult(amount);
  const grow = preview ? 1.7 : 1;
  const items = [];
  for (const kind of kinds) {
    const k = KINDS[kind];
    if (!k) continue;
    const asset = k.src ? assets[k.src] : null;
    if (k.src && !asset) continue;
    const n = Math.max(1, Math.round(k.count * (k.motion === 'smoke' || k.motion === 'light' ? 1 : mult)));
    if (k.motion === 'smoke') {
      // Puffs: soft-edged pieces of the owner's smoke picture, each its own size and place.
      const img = asset.image;
      const iw = img.width;
      const ih = img.height;
      for (let i = 0; i < n; i++) {
        const cw = Math.round(iw * lerp(0.45, 0.7, r()));
        const ch = Math.round(cw * lerp(0.7, 1.0, r()));
        const cx = Math.round(r() * (iw - cw));
        const cy = Math.round(r() * (ih - ch));
        const c = makeCanvas(cw, ch);
        const g = c.getContext('2d');
        g.drawImage(img, cx, cy, cw, ch, 0, 0, cw, ch);
        g.globalCompositeOperation = 'destination-in';
        const grad = g.createRadialGradient(cw / 2, ch / 2, 0, cw / 2, ch / 2, Math.min(cw, ch) / 2);
        grad.addColorStop(0, 'rgba(0,0,0,1)');
        grad.addColorStop(0.55, 'rgba(0,0,0,0.75)');
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        g.fillStyle = grad;
        g.fillRect(0, 0, cw, ch);
        const w = unit() * lerp(0.75, 1.15, r());
        items.push({
          kind, k, motion: 'smoke', image: c, sx: 0, sy: 0, sw: cw, sh: ch, w, h: (w * ch) / cw,
          x0: lerp(0.05, 0.95, r()) * F.w, y0: lerp(0.3, 1.25, r()) * F.h,
          rise: lerp(160, 300, r()), drift: lerp(-60, 60, r()), rot: lerp(-0.4, 0.4, r()), spin: lerp(-0.25, 0.25, r()),
          // Staggered evenly, so as one puff thins another thickens elsewhere.
          ph: (i + r() * 0.5) / n, alpha: lerp(0.45, 0.7, r()) * amountAlpha(amount), z: r(),
        });
      }
      continue;
    }
    if (k.draw === 'ember') {
      // The fire below the frame the sparks come off: a low warm glow that flickers.
      items.push({ kind, k, motion: 'firelight', z: 0, ph: r(), gain });
    }
    if (k.motion === 'pane') {
      items.push({ kind, k, motion: 'pane', image: asset.image, z: 0.05, ph: 0, gain });
      continue;
    }
    if (k.motion === 'light') {
      items.push({ kind, k, motion: 'beam', image: asset.image, z: 0, ph: 0, gain });
      for (let i = 0; i < n; i++) {
        items.push({ kind, k, motion: 'ray', x0: lerp(-0.42, 0.42, (i + r()) / n), width: lerp(14, 42, r()), len: lerp(220, 420, r()), ph: r(), alpha: lerp(0.25, 0.5, r()), z: 0.1, gain });
      }
      continue;
    }
    for (let i = 0; i < n; i++) {
      const z = r();
      const [ux, uy] = k.src && k.solid ? spot(r) : [r(), r()];
      // Solid things (paper, cards, metal) are never see-through: depth shows in their size. Light things fade with depth.
      const it = { kind, k, motion: k.motion, z, ph: r(), x0: ux * F.w, y0: uy * F.h, alpha: k.solid ? 1 : lerp(0.6, 1, z) };
      if (k.draw === 'ember') {
        // A spark lives 0.9-2.5 s (a whole number of lives a loop, so the loop meets itself), climbing 500-1000 px a
        // second at up to ~25 degrees off straight up, wobbling in the heat. Born low; most burn out before the top.
        const lives = 3 + Math.floor(r() * 6);
        const speed = lerp(500, 1000, r());
        const tilt = (r() * 2 - 1) * 0.45;
        const life = 7.5 / lives;
        const hue = r();
        Object.assign(it, {
          x0: lerp(-0.05, 1.05, r()) * F.w, y0: lerp(0.35, 1.1, r()) * F.h,
          rise: speed * life * Math.cos(tilt), drift: speed * life * Math.sin(tilt), lives,
          sway: lerp(6, 26, r()), wob: 2 + Math.floor(r() * 3), flick: 8 + Math.floor(r() * 10),
          len: speed * 0.045, width: lerp(3, 5.5, z) * grow,
          // Orange, deep red, hot yellow, and pale ash-white streaks (dimmer), as in the reference.
          color: hue < 0.4 ? [255, 132, 40] : hue < 0.6 ? [222, 52, 24] : hue < 0.75 ? [255, 214, 90] : [205, 196, 186],
          dim: hue >= 0.75 ? 0.55 : 1,
        });
      } else if (k.draw === 'dot') {
        it.size = lerp(k.size[0], k.size[1], z) * grow;
      } else if (k.draw === 'mote') {
        it.size = lerp(k.size[0], k.size[1], z) * grow;
        // It glints 2-4 times a loop as it turns (a whole number, so the loop meets itself).
        it.glint = 2 + Math.floor(r() * 3);
      } else if (k.draw === 'confetti') {
        it.size = lerp(k.size[0], k.size[1], z) * grow;
        it.color = ['#F4BE52', '#ff4f8b', '#3fd0ff', '#ffffff', '#8b5cf6', '#46e08a'][Math.floor(r() * 6)];
      } else {
        const p = asset.pieces[Math.floor(r() * asset.pieces.length)];
        const [sx, sy, sw, sh] = p;
        const longest = Math.max(sw, sh);
        let side = k.ref ? longest * (unit() / asset.refWidth) * lerp(k.size[0], k.size[1], z) : lerp(k.size[0], k.size[1], z) * unit();
        // Never past 1.5x its own pixels; the picker's small tiles show specks larger so the kind reads at all.
        side = preview ? side * grow * (k.ref ? 2.5 : 1) : Math.min(side, longest * (k.maxScale || 1.5));
        Object.assign(it, { image: asset.image, sx, sy, sw, sh, w: (side * sw) / longest, h: (side * sh) / longest });
      }
      if (it.motion === 'fall') {
        it.fall = lerp(k.fall[0], k.fall[1], z);
        it.sway = (k.sway || 0) * lerp(0.5, 1, r());
        it.rot = (r() * 2 - 1) * (k.turn ?? 3.1);
        it.spin = (r() * 2 - 1) * (k.spin || 0);
        it.flip = k.tumble ? lerp(0.6, 1.4, r()) : 0;
        if (k.through) {
          // Off the frame by its own size at both ends; spread evenly down the loop so the frame is never empty.
          it.through = true;
          it.margin = Math.max(it.w || 0, it.h || 0) * 0.8 + it.sway;
          it.ph = (i + r() * 0.7) / n;
        }
      }
      if (it.motion === 'still') it.rot = (r() * 2 - 1) * (k.turn || 0);
      if (k.burn) it.flick = 4 + Math.floor(r() * 4);
      items.push(it);
    }

  }
  // Far things first; the beam behind everything.
  items.sort((a, b) => a.z - b.z);
  // light: the picture's light map (lightMap), for the kinds seen only in the light. None (the picker's tiles, no
  // picture): they show everywhere.
  return { items, solid: items.some((i) => i.k.solid), light, opacity: opacity || {}, place: place || {}, frame: F, makeCanvas };
}

/** A whole-frame picture (the beam, the panes) cover-fit into the frame, never stretched. */
function coverDraw(ctx, image) {
  const k = Math.max(F.w / image.width, F.h / image.height);
  const w = image.width * k;
  const h = image.height * k;
  ctx.drawImage(image, (F.w - w) / 2, (F.h - h) / 2, w, h);
}

/** Where a thing is at time t (seconds), plus an optional push (ox, oy) from the cursor. */
function pose(it, t) {
  const loop = (t / LOOP_S) % 1;
  if (it.motion === 'fall') {
    const u = (loop + it.ph) % 1;
    if (it.through) {
      return {
        x: it.x0 + it.sway * Math.sin(TAU * (u + it.ph)), y: -it.margin + (F.h + 2 * it.margin) * u,
        rot: it.rot + it.spin * Math.sin(TAU * u * 0.5 + it.ph), sy: it.flip ? 0.55 + 0.45 * Math.abs(Math.cos(TAU * u * 0.5 * it.flip + it.ph * 3)) : 1,
        a: it.alpha, u,
      };
    }
    return {
      x: it.x0 + it.sway * Math.sin(TAU * (u + it.ph)), y: it.y0 + it.fall * (u - 0.5),
      rot: it.rot + it.spin * Math.sin(TAU * u * 0.5 + it.ph), sy: it.flip ? 0.55 + 0.45 * Math.abs(Math.cos(TAU * u * 0.5 * it.flip + it.ph * 3)) : 1,
      a: it.alpha * envelope(u) * (it.glint ? 0.45 + 0.55 * Math.pow(0.5 + 0.5 * Math.sin(TAU * (u * it.glint + it.ph)), 2) : 1), u,
    };
  }
  if (it.motion === 'rise') {
    const u = (loop * it.lives + it.ph) % 1;
    const wob = it.wob || 1.5;
    const w = TAU * (u * wob + it.ph);
    // Heading: up the climb plus the wobble's own sideways speed (the streak points the way the spark flies).
    const vx = it.drift + it.sway * TAU * wob * Math.cos(w);
    const vy = -it.rise;
    return {
      x: it.x0 + it.drift * u + it.sway * Math.sin(w), y: it.y0 - it.rise * u,
      rot: Math.atan2(vy, vx) + Math.PI / 2, sy: 1, u,
      a: it.alpha * (it.dim || 1) * envelope(u, 0.04, 0.5) * (0.55 + 0.45 * Math.abs(Math.sin(TAU * (u * it.flick + it.ph)))),
    };
  }
  if (it.motion === 'smoke') {
    const u = (loop + it.ph) % 1;
    return {
      x: it.x0 + it.drift * u, y: it.y0 - it.rise * u, rot: it.rot + it.spin * u, s: 1 + 0.35 * u,
      a: it.alpha * Math.pow(Math.sin(Math.PI * u), 1.3), u,
    };
  }
  if (it.motion === 'ray') {
    const u = (loop + it.ph) % 1;
    return { u, a: it.alpha * envelope(u, 0.25, 0.35) };
  }
  if (it.motion === 'beam') return { a: 0.9 + 0.1 * Math.sin(TAU * loop * 2) };
  if (it.motion === 'pane') return { a: 1 };
  if (it.motion === 'firelight') return { a: 0.6 + 0.1 * Math.sin(TAU * (loop * 6 + it.ph)) + 0.06 * Math.sin(TAU * (loop * 11 + it.ph * 3)) };
  return { x: it.x0, y: it.y0, rot: it.rot || 0, sy: 1, a: it.alpha };
}

/**
 * Draw the scene at time t into target (a 2D context already scaled to the
 * 1080x1920 frame). which: 'light' (things that add light: smoke, embers,
 * glass, dust...), 'solid' (bills, cards, metal) or 'all'. push(i) gives a
 * cursor offset {x, y} for item i (the page), or nothing (the download).
 */
export function drawScene(target, scene, t, { which = 'all', push = null } = {}) {
  F = scene.frame || FRAME;
  const loop = (t / LOOP_S) % 1;
  // What paint draws on: the target, or a moved kind's own layer (below).
  let ctx = target;
  const moved = (kind) => (scene.place && !placeIsNone(scene.place[kind]) ? scene.place[kind] : null);
  const shown = (it) => !((which === 'light' && it.k.solid) || (which === 'solid' && !it.k.solid));
  const one = (it, i, box) => {
    const p = pose(it, t);
    const off = push ? push(i) : null;
    const ox = off ? off.x : 0;
    const oy = off ? off.y : 0;
    // Seen only in the light: where the speck is now (with the cursor's push), where its kind was put.
    if (it.k.lit && scene.light) p.a *= litAt(scene.light, box ? box.left + box.s * (p.x + ox) : p.x + ox, box ? box.top + box.s * (p.y + oy) : p.y + oy);
    const total = p.a * (it.gain ?? 1) * (scene.opacity?.[it.kind] ?? 1);
    if (!(total > 0.003)) return;
    // Light adds: past full strength it is laid on a second time (the slider above the middle on a one-picture kind).
    const passes = it.k.light ? Math.min(2, Math.ceil(total)) : 1;
    for (let pass = 0; pass < passes; pass++) paint(it, p, ox, oy, Math.min(1, total - pass));
  };
  scene.items.forEach((it, i) => {
    if (shown(it) && !moved(it.kind)) one(it, i, null);
  });
  // A kind moved or resized (placeBox): drawn whole in its own frame on a layer the target's size, its edges inside the
  // frame softened (as canvas.js softens a clip's), then laid on where it was put, as it would have been drawn.
  for (const kind of [...new Set(scene.items.filter(shown).map((it) => it.kind))]) {
    const at = moved(kind);
    if (!at || !scene.makeCanvas) continue;
    const box = placeBox(at, F);
    const m = typeof target.getTransform === 'function' ? target.getTransform() : { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
    const cw = (target.canvas && target.canvas.width) || Math.ceil(F.w * m.a + m.e);
    const ch = (target.canvas && target.canvas.height) || Math.ceil(F.h * m.d + m.f);
    if (!scene.layer || scene.layer.width !== cw || scene.layer.height !== ch) scene.layer = scene.makeCanvas(cw, ch);
    const g = scene.layer.getContext('2d');
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = 'source-over';
    g.globalAlpha = 1;
    g.clearRect(0, 0, cw, ch);
    g.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
    ctx = g;
    scene.items.forEach((it, i) => {
      if (it.kind === kind && shown(it)) one(it, i, box);
    });
    ctx = target;
    // The soft edges, in the layer's own frame (before it is scaled): each a fade to nothing over its width.
    g.globalCompositeOperation = 'destination-in';
    for (const [a, b, len, horiz] of [[box.fl, box.fr, F.w, true], [box.ft, box.fb, F.h, false]]) {
      if (!(a > 0 || b > 0)) continue;
      const grad = horiz ? g.createLinearGradient(0, 0, F.w, 0) : g.createLinearGradient(0, 0, 0, F.h);
      grad.addColorStop(0, a > 0 ? 'rgba(0,0,0,0)' : '#000');
      grad.addColorStop(Math.min(0.5, a / box.s / len), '#000');
      grad.addColorStop(Math.max(0.5, 1 - b / box.s / len), '#000');
      grad.addColorStop(1, b > 0 ? 'rgba(0,0,0,0)' : '#000');
      g.fillStyle = grad;
      g.fillRect(0, 0, F.w, F.h);
    }
    g.globalCompositeOperation = 'source-over';
    // Laid on: the target's own pixels back to the frame, the frame to where the kind was put, then to the target's pixels.
    const det = m.a * m.d - m.b * m.c || 1;
    const inv = [m.d / det, -m.b / det, -m.c / det, m.a / det, (m.c * m.f - m.d * m.e) / det, (m.b * m.e - m.a * m.f) / det];
    target.save();
    target.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
    // Turned about its middle (placeTurn), as the page's CSS and canvas.js clipPlaceTurned turn a clip.
    const turn = (placeTurn(at) * Math.PI) / 180;
    if (turn) {
      const cx = box.left + box.w / 2;
      const cy = box.top + box.h / 2;
      target.translate(cx, cy);
      target.rotate(turn);
      target.translate(-cx, -cy);
    }
    target.transform(box.s, 0, 0, box.s, box.left, box.top);
    target.transform(...inv);
    target.globalAlpha = 1;
    target.globalCompositeOperation = KINDS[kind] && KINDS[kind].light ? 'lighter' : 'source-over';
    target.drawImage(scene.layer, 0, 0);
    target.restore();
  }

  function paint(it, p, ox, oy, alpha) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.globalCompositeOperation = it.k.light ? 'lighter' : 'source-over';
    if (it.motion === 'beam') {
      coverDraw(ctx, it.image);
    } else if (it.motion === 'firelight') {
      // The fire below: a dim orange-brown glow rising over the bottom ~45%, as in the owner's reference.
      const g = ctx.createLinearGradient(0, F.h, 0, F.h * 0.55);
      g.addColorStop(0, 'rgba(150,62,22,0.8)');
      g.addColorStop(0.4, 'rgba(92,34,12,0.42)');
      g.addColorStop(1, 'rgba(40,12,4,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, F.h * 0.55, F.w, F.h * 0.45);
    } else if (it.motion === 'ray') {
      // A ray slides down inside the beam, spreading as the beam does.
      const top = 70 + p.u * F.h * 0.42;
      const x = F.w / 2 + it.x0 * F.w * (0.35 + top / F.h);
      const g = ctx.createLinearGradient(0, top, 0, top + it.len);
      g.addColorStop(0, 'rgba(255,248,235,0)');
      g.addColorStop(0.5, 'rgba(255,246,230,0.55)');
      g.addColorStop(1, 'rgba(255,240,225,0)');
      ctx.fillStyle = g;
      ctx.translate(x + ox, top + oy);
      ctx.rotate(-it.x0 * 0.5);
      ctx.fillRect(-it.width / 2, 0, it.width, it.len);
    } else if (it.motion === 'smoke') {
      ctx.translate(p.x + ox, p.y + oy);
      ctx.rotate(p.rot);
      ctx.scale(p.s, p.s);
      ctx.drawImage(it.image, it.sx, it.sy, it.sw, it.sh, -it.w / 2, -it.h / 2, it.w, it.h);
    } else if (it.motion === 'pane') {
      coverDraw(ctx, it.image);
    } else if (it.k.draw === 'ember') {
      // A spark: a thin bright streak, hot at its head and fading behind it, pointing the way it flies; it shortens and dims as it cools.
      const [cr, cg, cb] = it.color;
      const len = it.len * (1 - 0.45 * p.u);
      ctx.translate(p.x + ox, p.y + oy);
      ctx.rotate(p.rot);
      const g = ctx.createLinearGradient(0, 0, 0, len);
      g.addColorStop(0, `rgba(${Math.min(255, cr + 30)},${Math.min(255, cg + 60)},${Math.min(255, cb + 60)},1)`);
      g.addColorStop(0.35, `rgba(${cr},${cg},${cb},0.85)`);
      g.addColorStop(1, `rgba(${cr},${Math.round(cg * 0.6)},${cb},0)`);
      ctx.fillStyle = g;
      ctx.fillRect(-it.width / 2, 0, it.width, len);
    } else if (it.k.draw === 'dot') {
      ctx.fillStyle = 'rgba(255,252,244,1)';
      ctx.beginPath();
      ctx.arc(p.x + ox, p.y + oy, it.size, 0, TAU);
      ctx.fill();
    } else if (it.k.draw === 'mote') {
      // A speck of dust catching the light: a bright point with a soft warm falloff.
      const rad = it.size * 2;
      const g = ctx.createRadialGradient(p.x + ox, p.y + oy, 0, p.x + ox, p.y + oy, rad);
      g.addColorStop(0, 'rgba(255,250,238,1)');
      g.addColorStop(0.3, 'rgba(255,244,222,0.55)');
      g.addColorStop(1, 'rgba(255,236,206,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(p.x + ox, p.y + oy, rad, 0, TAU);
      ctx.fill();
    } else if (it.k.draw === 'confetti') {
      ctx.translate(p.x + ox, p.y + oy);
      ctx.rotate(p.rot);
      ctx.scale(1, p.sy);
      ctx.fillStyle = it.color;
      ctx.fillRect(-it.size / 2, -it.size / 4, it.size, it.size / 2);
    } else {
      ctx.translate(p.x + ox, p.y + oy);
      ctx.rotate(p.rot);
      ctx.scale(1, p.sy);
      ctx.drawImage(it.image, it.sx, it.sy, it.sw, it.sh, -it.w / 2, -it.h / 2, it.w, it.h);
      if (it.k.burn) {
        // The fire lives: the flames flare and dim, and sparks lift off the burning edge.
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = alpha * (0.25 + 0.25 * Math.sin(TAU * (loop * it.flick + it.ph)));
        ctx.drawImage(it.image, it.sx, it.sy, it.sw, it.sh, -it.w / 2, -it.h / 2, it.w, it.h);
        for (let s = 0; s < 4; s++) {
          const v = (loop * 3 + it.ph + s / 4) % 1;
          const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 6);
          g.addColorStop(0, 'rgba(255,240,190,1)');
          g.addColorStop(1, 'rgba(255,90,0,0)');
          ctx.save();
          ctx.globalAlpha = alpha * envelope(v, 0.1, 0.5);
          ctx.translate((s - 1.5) * it.w * 0.12 + Math.sin(TAU * (v + s)) * 10, -it.h * 0.45 - v * 140);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(0, 0, 6, 0, TAU);
          ctx.fill();
          ctx.restore();
        }
      }
    }
    ctx.restore();
  }
}

/** The things near a point, for the page's cursor air: their index and where they are now. */
export function positions(scene, t) {
  F = scene.frame || FRAME;
  return scene.items.map((it) => {
    const p = pose(it, t);
    const x = p.x ?? F.w / 2;
    const y = p.y ?? 0;
    // Where it shows: a moved kind's things are where it was put (placeBox).
    const at = scene.place && !placeIsNone(scene.place[it.kind]) ? placeBox(scene.place[it.kind], F) : null;
    return { x: at ? at.left + at.s * x : x, y: at ? at.top + at.s * y : y, a: p.a, z: it.z, moves: !['beam', 'ray', 'still', 'firelight'].includes(it.motion) };
  });
}
